import { useEffect, useRef } from 'react';
import { useLocationGroupStore, useSelectedScope } from '@frontend/scope';
import { useWorkspaceShallowStore, useWorkspaceStore } from '../store';

/**
 * Clears workspace selection when user exits workspace mode or switches orgs.
 *
 * Listens to scope changes and automatically clears the workspace ID when:
 * 1. User switches from workspace mode to any other mode (location/division/org)
 * 2. User switches to a different organization (workspaces belong to an org)
 *
 * **Why this is needed:**
 * - Prevents "stale workspace" state where workspace ID lingers after exiting workspace mode
 * - Prevents cross-org workspace contamination (workspace from Org A applied in Org B)
 * - Ensures workspace selection is only active during workspace mode in the correct org
 * - Automatically persists to localStorage via Zustand persist middleware
 *
 * **Architecture:**
 * - Workspace feature manages its own cleanup (separation of concerns)
 * - No circular dependencies (workspace can depend on scope, one direction)
 * - Scope module doesn't need to know about workspace module
 *
 * @example
 * // In AppProviders or similar top-level component:
 * useWorkspaceScopeListener();
 */
export function useWorkspaceScopeListener() {
  const selectedScope = useSelectedScope();
  const selectedOrgId = useLocationGroupStore((state) => state.selectedOrgId);
  const setSelectedWorkspaceId = useWorkspaceStore((state) => state.setSelectedWorkspaceId);
  const { selectedWorkspaceId } = useWorkspaceShallowStore('selectedWorkspaceId');
  const prevOrgIdRef = useRef<string>();

  useEffect(() => {
    // Always track org changes even when no workspace is selected, so the diff is
    // correct if a workspace is later selected in the same session.
    const orgChanged = prevOrgIdRef.current !== undefined && prevOrgIdRef.current !== selectedOrgId;
    prevOrgIdRef.current = selectedOrgId;

    // Guard: nothing to clear when no workspace is selected.
    //
    // The store's set() has no equality check — every call creates a new Zustand state
    // object, notifies all subscribers, and writes to localStorage via the persist
    // middleware. For the vast majority of users who never use workspaces, calling
    // setSelectedWorkspaceId(null) when it is already null on every scope change is
    // pure waste and causes spurious re-renders.
    if (!selectedWorkspaceId) return;

    // Clear workspace if:
    // 1. User exits workspace mode (scope type changes)
    // 2. User switches to a different org (workspace belongs to an org)
    const shouldClearWorkspace = (selectedScope && selectedScope.scopeType !== 'workspace') || orgChanged;

    if (shouldClearWorkspace) {
      setSelectedWorkspaceId(null);
    }
  }, [selectedScope, selectedOrgId, selectedWorkspaceId, setSelectedWorkspaceId]);
}
